import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Check, ChevronDown, CircleX, Star } from "lucide-react"
import { twMerge } from "tailwind-merge"
import { motion } from "framer-motion"

import useClickOutside from "@src/hooks/useClickOutside"
import LoadingSpinner from "./LoadingSpinner"
import { createId, isObject } from "@src/utils"
import Input from "./Input"
import Text from "./Text"

interface Props<T> {
  // Required
  id: string
  className?: string
  options: T[]
  value: string
  as?: "search" | "select"
  children?: (item: T, index?: number) => React.ReactNode
  reduceSearch?: (item: T) => string
  reduceLabel: (item: T) => string
  reduceValue: (item: T) => string
  onChange: (item: T) => void
  reduce?: (item: T) => void
  reduceOption?: (item: T, index?: number) => React.ReactNode
  reduceIsSelected?: (item: T, index?: number) => string
  position?: "to-top" | "to-bottom"
  initSearch?: string
  IconEnd?: React.ReactNode
  noClear?: boolean
  inputSize?: string
  fontSize?: string
  listItemSize?: string
  iconSize?: string
  placeholder?: string
  readOnly?: boolean
  maxLength?: number
  wrapperRender?: "PANEL" | "MODAL"
  label?: string
  error?: string
  viewInputSearchOffline?: boolean
  classNameOption?: string
  disabled?: boolean
  loading?: boolean
}

interface OptionProps {
  onClick: () => void
  className?: string
  children: React.ReactNode
  isSelected?: boolean
}

interface TSelectContext {
  open: boolean
  toggle: React.Dispatch<React.SetStateAction<boolean>>
  onSelected: (value: any) => void
}

interface IDropdownPosition {
  top: string
  left: string
  width: string
}

const SelectContext = createContext<TSelectContext | null>(null)

export function Select<K>(_props_: Props<K>): React.ReactElement {
  const {
    children,
    options = [],
    as = "select",
    reduceLabel,
    reduceOption,
    onChange,
    initSearch = "",
    IconEnd,
    noClear = false,
    placeholder,
    listItemSize,
    inputSize,
    iconSize = "",
    maxLength = 255,
    wrapperRender = "MODAL",
    loading = false,
    reduceIsSelected,
    id = createId(),
    label,
    error,
    classNameOption = "",
    reduceValue,
    reduceSearch,
    value,
    ...props
  } = _props_
  const [dropdownPosition, setDropdownPosition] = useState<IDropdownPosition | null>(null)
  const [searchOffline, setSearchOffline] = useState<string>("")
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [open, setOpen] = useState<boolean>(false)

  const itemAdapted = useCallback(
    (item: K) => {
      if (reduceSearch) return reduceSearch(item)
      return reduceLabel(item)
    },
    [reduceSearch, reduceLabel],
  )

  const filteredOptions = useMemo(() => {
    const searchOfflineTrimmed = searchOffline.trim().toLocaleLowerCase()
    if (as === "search" && searchOfflineTrimmed.length) {
      return options.filter((item) => itemAdapted(item).toLocaleLowerCase().includes(searchOfflineTrimmed))
    }
    return options
  }, [as, options, searchOffline, itemAdapted])

  const dropdownPositionHandler = useCallback(() => {
    if (containerRef.current && open && !dropdownPosition) {
      const rect = containerRef.current.getBoundingClientRect()
      const top = rect.bottom + 2
      setDropdownPosition({
        top: `${top}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
      })
    }
  }, [containerRef.current, open, dropdownPosition])

  useEffect(() => {
    dropdownPositionHandler()
    setTimeout(() => {
      const button = containerRef.current?.querySelector('[data-selected="true"]')
      if (button) {
        button.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }, 0)
  }, [open])

  useEffect(() => {
    const listener = () => {
      dropdownPositionHandler()
      setDropdownPosition(null)
    }
    window.addEventListener("resize", listener)
    return () => window.removeEventListener("resize", listener)
  }, [])

  useClickOutside(containerRef, () => {
    if (open) {
      const normalizeSearch = String(searchOffline).trim().toLocaleUpperCase()
      if (normalizeSearch == String(initSearch).trim().toLocaleUpperCase()) {
        setOpen(false)
      } else {
        const item = filteredOptions.find(
          (option) => reduceLabel(option).trim().toLocaleUpperCase() == normalizeSearch,
        )
        if (filteredOptions.length && !!item) {
          onSelected(item)
        } else {
          resetState()
        }
      }
    }
    setSearchOffline("")
  })

  const onSelected = (item: K) => {
    onChange(item)
    setOpen(false)
    setSearchOffline("")
  }

  const getInitialValue = () => {
    const first = options[0]
    if (!first) {
      console.warn("😡 No provided options")
      return ""
    }
    if (isObject(first)) {
      return {}
    }
    return ""
  }

  const resetState = () => {
    onChange(getInitialValue() as K)
    setOpen(false)
    setSearchOffline("")
  }

  const onResetKeyEscape = () => {
    setSearchOffline("")
    setOpen(false)
  }

  const getKey = useCallback(
    (item: K) => {
      const firstItem = options[0]
      if (!firstItem) return createId()
      if (typeof firstItem === "string") {
        return item as string
      } else if (isObject(firstItem)) {
        return reduceValue(item)
      }
      return createId()
    },
    [options, reduceValue],
  )

  const renderOption = useCallback(
    (item: K, index: number) => {
      const currentKey = getKey(item)
      const isSelected = reduceValue(item) == value
      return (
        <Option
          className={classNameOption}
          key={currentKey}
          isSelected={isSelected}
          onClick={() => onSelected(item)}
        >
          {reduceOption?.(item) ?? children?.(item, index) ?? reduceLabel(item)}
        </Option>
      )
    },
    [children, reduceOption, reduceIsSelected, getKey, reduceValue, searchOffline, value],
  )

  const cssListItemSizeClasses = useMemo(() => {
    return twMerge(
      "flex flex-col border rounded-lg overflow-y-auto scroll__styled shadow-lg dark:border-slate-700",
      listItemSize ?? "max-h-[250px]",
    )
  }, [listItemSize])

  const valueToDisplay = useMemo(() => {
    const found = options.find((op) => reduceValue(op) == value)
    return found ? reduceLabel(found) : ""
  }, [reduceValue, reduceLabel, options, value])

  return (
    <>
      {!!label && (
        <Text as="label" color="legacy-dark" className="mb-0" htmlFor={id}>
          {label}
        </Text>
      )}
      <div className="flex-1 w-full">
        <div className={twMerge("-z-1 w-full h-full", open ? "relative" : "")} ref={containerRef}>
          <SelectContext.Provider value={{ open, toggle: setOpen, onSelected }}>
            <div className="flex h-full">
              <div className="group/item relative flex-1">
                <Input
                  placeholder={placeholder ?? `-- Select --`}
                  onClick={() => setOpen(!open)}
                  maxLength={maxLength}
                  errorStyled={!!error}
                  autoComplete="off"
                  value={valueToDisplay}
                  readOnly
                  id={id}
                  {...props}
                />
                <div className="pointer-events-none absolute inset-y-0 right-1 flex items-center px-0.5 text-gray-400 gap-0.5">
                  {!noClear && !!valueToDisplay.length && (
                    <CircleX
                      strokeWidth={2.25}
                      className={twMerge(
                        "text-sm relative pointer-events-auto select-none text-gray-300 hover:!text-red-600 cursor-pointer transition-all duration-100",
                        "group-hover/item:text-gray-500",
                      )}
                      onClick={resetState}
                      size={14}
                    />
                  )}
                  {loading && (
                    <div className="-translate-x-1 leading-none">
                      <LoadingSpinner size="sm" />
                    </div>
                  )}
                  <ChevronDown
                    className={twMerge(iconSize, open ? "rotate-180" : "", "transition-all duration-300")}
                    size={14}
                  />
                </div>
              </div>
              {IconEnd}
            </div>
            {error && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-sm text-red-600 dark:text-red-400"
              >
                {error}
              </motion.p>
            )}
            {open && (
              <motion.div
                style={dropdownPosition ?? undefined}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className={twMerge(
                  cssListItemSizeClasses,
                  wrapperRender === "MODAL" ? "fixed z-[1051]" : "z-[1051]",
                )}
              >
                {as === "search" && (
                  <div className="sticky top-0 w-full p-0.5 pt-1 px-1 first-of-type:rounded-t last-of-type:rounded-b bg-white dark:bg-slate-800">
                    <Input
                      placeholder="Search"
                      className="w-full! focus:ring"
                      autoFocus
                      value={searchOffline}
                      onKeyDown={(ev: React.KeyboardEvent<HTMLInputElement>) => {
                        if (ev.key === "Escape") {
                          onResetKeyEscape()
                        }
                      }}
                      onChange={(ev) => setSearchOffline(ev.target.value)}
                    />
                  </div>
                )}
                {!!filteredOptions.length ? (
                  filteredOptions.map(renderOption)
                ) : (
                  <div className="flex bg-white dark:bg-slate-800 items-center justify-center h-full p-3">
                    {searchOffline.length ? (
                      <span className="text-xs text-gray-400">
                        No results found
                        {!!searchOffline.length && (
                          <>
                            {" "}
                            for <strong>"{searchOffline}"</strong>
                          </>
                        )}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">No options</span>
                    )}
                  </div>
                )}
              </motion.div>
            )}
          </SelectContext.Provider>
        </div>
      </div>
    </>
  )
}

export function Option({ onClick, children, className, isSelected = false, ...props }: OptionProps) {
  return (
    <button
      className={twMerge(
        isSelected && "!bg-gray-100 dark:!bg-slate-700",
        "flex-1 w-full bg-white hover:bg-gray-100 p-3 text-sm text-start pointer-events-auto dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700",
        className,
      )}
      data-selected={isSelected}
      onClick={onClick}
      type="button"
      {...props}
    >
      <div className="flex items-center gap-1 w-full">
        <div className="flex-1 truncate">{children}</div>{" "}
        {isSelected && <Check size={16} strokeWidth={4} className="text-gray-400" />}
      </div>
    </button>
  )
}

interface SelectWrapperProps {
  children: React.ReactNode
  className?: string
  classNameLabel?: string
  label?: string
  required?: boolean
  htmlFor?: string
  error?: string
  withTranslate?: boolean
}

export const SelectWrapper = ({
  children,
  className,
  label,
  required,
  error,
  withTranslate = false,
  classNameLabel,
  ...props
}: SelectWrapperProps) => {
  return (
    <div className={twMerge("flex flex-col", className)}>
      <div className="leading-tight">
        {label && (
          <label {...props} className={twMerge("text-xs", classNameLabel)}>
            {label} {required && <Star />}
          </label>
        )}
      </div>
      {children}
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  )
}
